import {
  Component,
  OnInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { take } from 'rxjs/operators';
import { AppService } from '../../../app.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { AdminGridDirective, AdminGridConfig, AdminGridCallbackEvent } from '../../shared/admin-grid.directive';
import { normalizeUserExpiryRecords } from './access-renewal.service';
import { UserExpiryReportModalComponent } from './user-expiry-report-modal/user-expiry-report-modal.component';

/**
 * Angular 12 migration of admin.user-expiry-report (Access Renewal).
 * Original: loadGridJson?action_id=33298&grid_id=6129
 * Filter bar: refresh, client dropdown.
 * Grid callback: opens user expiry modal on form (Edit).
 */
@Component({
    selector: 'gtw-access-renewal',
    templateUrl: './access-renewal.component.html',
    styleUrls: ['./access-renewal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AccessRenewalComponent implements OnInit {
  @ViewChild(AdminGridDirective) adminGrid!: AdminGridDirective;

  gridConfig!: AdminGridConfig;
  loading = false;
  clients: any[] = [];
  selectedClientKey = '';

  private readonly ACTION_ID = 33298;
  private readonly GRID_ID = '6129';

  constructor(
    private cdr: ChangeDetectorRef,
    private appService: AppService,
    private dialog: MatDialog,
    private snackbar: GtwSnackbarService,
  ) {}

  ngOnInit(): void {
    this.setupClients();
    this.buildGridConfig();
  }

  onGridCallback(event: AdminGridCallbackEvent): void {
    if (event.type === 'form') {
      this.openUserExpiryModal(event.data);
    }
  }

  onGridDataLoaded(_data: any): void {
    this.cdr.detectChanges();
  }

  refresh(): void {
    this.adminGrid?.clearCache();
    this.adminGrid?.refresh({ client_key: this.selectedClientKey });
  }

  onClientChange(): void {
    this.gridConfig = {
      ...this.gridConfig,
      extraParams: { client_key: this.selectedClientKey },
    };
    this.adminGrid?.clearCache();
    this.adminGrid?.refresh({ client_key: this.selectedClientKey });
  }

  private openUserExpiryModal(gridPayload: any): void {
    const records = normalizeUserExpiryRecords(gridPayload);
    if (records.length < 1) {
      this.snackbar.openSnackBar(
        'No users selected. Select one or more rows and click Edit.',
        'gtw-snackbar--red',
      );
      return;
    }

    const config = new MatDialogConfig();
    config.panelClass = 'admin-modal-panel';
    config.width = '800px';
    config.maxWidth = '90vw';
    config.disableClose = true;
    config.data = {
      gridPayload,
      clientKey: this.selectedClientKey,
    };

    const ref = this.dialog.open(UserExpiryReportModalComponent, config);
    ref.afterClosed().pipe(take(1)).subscribe((result) => {
      if (result === 'saved') {
        this.adminGrid?.clearCache();
        this.adminGrid?.refresh({ client_key: this.selectedClientKey });
        this.cdr.detectChanges();
      }
    });
  }

  private setupClients(): void {
    const user = this.appService.USER_SETTINGS?.user;
    const userClients = user?.clients;
    const userClient = user?.client;

    if (Array.isArray(userClients) && userClients.length > 0) {
      this.clients = userClients;
    } else if (userClient?.client_key !== undefined) {
      this.clients = [userClient];
    }

    const clientKey = user?.client?.client_key ?? user?.client_key;
    if (clientKey != null && clientKey !== '') {
      this.selectedClientKey = String(clientKey);
    } else if (this.clients.length > 0) {
      this.selectedClientKey = String(this.clients[0].client_key);
    }
  }

  private buildGridConfig(): void {
    this.gridConfig = {
      actionId: this.ACTION_ID,
      gridId: this.GRID_ID,
      extraParams: { client_key: this.selectedClientKey },
      autoLoad: true,
      cacheKey: 'admin/access-renewal',
    };
  }
}
